import React, { useEffect, useRef, useState, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { createContext } from "use-context-selector";
import { isMobile } from "react-device-detect";

const ThemeContext = createContext();
export default ThemeContext;

const CSS_VERSION = "v2";

const loadedCssCache = new Set();

const getSkyRouteCssFile = (route) => {
  if (["exchange"].includes(route)) {
    return "/assets/css/sky/web.css";
  }
  return isMobile ? "/assets/css/sky/sky.css" : "/assets/css/sky/web.css";
};

const CSS_CONFIG = {
  DEFAULT: [
    "https://cdn.jsdelivr.net/npm/bootstrap@5.3.3/dist/css/bootstrap.min.css",
    "https://cdn.jsdelivr.net/npm/slick-carousel@1.8.1/slick/slick.css",
    "https://cdn.jsdelivr.net/npm/slick-carousel@1.8.1/slick/slick-theme.css",
    "/assets/css/App.css",
    "/assets/css/style.css",
  ],

  VELKI: [
    "/assets/css/icomoon.css",
    "/assets/css/velkie/velkieMedia.css",
    "/assets/css/velkie/velkie.css",
  ],

  SKY: [],
};

export const ThemeProvider = ({ children }) => {
  const location = useLocation();
  const [cssLoaded, setCssLoaded] = useState(false);

  const prevRouteGroupRef = useRef(null);
  const prevRouteRef = useRef(null);
  const isInitialMount = useRef(true);

  const getRouteGroup = useCallback((pathname) => {
    const route = pathname?.split("/")[1];

    if (
      [
        "sports-velki",
        "match-detail-velki",
        "league-velki",
        "favourite-velkie",
        "exchange-velki",
      ].includes(route)
    ) {
      return "VELKI";
    }

    if (
      [
        "sports-sky",
        "match-detail-sky",
        "inplay-sky",
        "horse-racing-sky",
        "favourite-sky",
        "sports-sky-new",
        "account-sky",
        "exchange",
        "result-sky",
      ].includes(route)
    ) {
      return "SKY";
    }

    return "DEFAULT";
  }, []);

  const addStylesheet = useCallback((href) => {
    return new Promise((resolve) => {
      const fullHref = `${href}?${CSS_VERSION}`;

      if (loadedCssCache.has(fullHref)) return resolve();

      if (document.querySelector(`link[href="${fullHref}"]`)) {
        loadedCssCache.add(fullHref);
        return resolve();
      }

      const link = document.createElement("link");
      link.rel = "stylesheet";
      link.href = fullHref;
      link.setAttribute("data-theme", "dynamic");

      link.onload = () => {
        loadedCssCache.add(fullHref);
        resolve();
      };

      link.onerror = () => {
        console.warn(`⚠️ CSS failed to load: ${href}`);
        resolve();
      };

      document.head.appendChild(link);
    });
  }, []);

  useEffect(() => {
    const loadCss = async () => {
      const currentGroup = getRouteGroup(location.pathname);
      document.body.classList.toggle("ck-app", currentGroup === "DEFAULT");
      document.documentElement.classList.toggle("ck-app-root", currentGroup === "DEFAULT");
      const currentRoute = location.pathname?.split("/")[1];

      let needsSkyCssReload = false;
      if (
        currentGroup === "SKY" &&
        prevRouteGroupRef.current === "SKY" &&
        !isInitialMount.current
      ) {
        needsSkyCssReload =
          getSkyRouteCssFile(prevRouteRef.current) !==
          getSkyRouteCssFile(currentRoute);
      }

      const groupChanged = prevRouteGroupRef.current !== currentGroup;

      if (!groupChanged && !isInitialMount.current && !needsSkyCssReload) {
        return;
      }

      const prevGroup = prevRouteGroupRef.current;
      isInitialMount.current = false;
      prevRouteGroupRef.current = currentGroup;
      prevRouteRef.current = currentRoute;

      setCssLoaded(false);

      document
        .querySelectorAll('link[data-theme="dynamic"]')
        .forEach((link) => {
          const href = link.getAttribute("href");

          if (needsSkyCssReload && href?.includes("/assets/css/sky/")) {
            link.remove();
            loadedCssCache.delete(href);
          }

          if (groupChanged) {
            link.remove();
            loadedCssCache.delete(href);
          }
        });

      try {
        if (currentGroup === "VELKI") {
          if (!isMobile) document.body.className = "is-desktop";

          document.documentElement.className =
            "canvas webgl supports csstransforms csstransforms3d avif apng webp s-webp no-jpeg2000 webp-animation webp-alpha webp-lossless";

          document.documentElement.style.setProperty(
            "--root-font-size",
            isMobile ? "3.7333333333333334vw" : "17.92px",
          );
        }

        if (currentGroup === "SKY" && isMobile) {
          document.body.className = "overfloxScroll";
        }

        const cssFiles = [...(CSS_CONFIG[currentGroup] ?? [])];

        if (currentGroup === "SKY") {
          if (isMobile) {
            cssFiles.push("/assets/css/icomoon.css");
            cssFiles.push(getSkyRouteCssFile(currentRoute));
          } else {
            cssFiles.push("/assets/css/sky/web.css");
          }

          if (["result-sky"].includes(currentRoute) && isMobile) {
            cssFiles.push("/assets/css/sky/result.css");
          }
        }

        await Promise.all(cssFiles.map(addStylesheet));

        await new Promise((resolve) => setTimeout(resolve, 100));

        setCssLoaded(true);
      } catch (error) {
        console.error("CSS load error:", error);
        setCssLoaded(true);
      }
    };

    loadCss();
  }, [location.pathname, getRouteGroup, addStylesheet]);

  return (
    <ThemeContext.Provider value={{ cssLoaded }}>
      {cssLoaded && children}
    </ThemeContext.Provider>
  );
};
